import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";

// Analytics in the v3.2 app: the account's own recording activity over the
// last 7, 30 or 90 days. Counts and durations only (no titles, transcripts
// or other visit content).
export const ACTIVITY_RANGES = [7, 30, 90] as const;
export const ownActivityQuerySchema = z.strictObject({
  days: z.coerce
    .number()
    .int()
    .refine(
      (value) => (ACTIVITY_RANGES as readonly number[]).includes(value),
      "days must be 7, 30 or 90",
    )
    .default(30),
});
export type OwnActivityQuery = z.output<typeof ownActivityQuerySchema>;
const seconds = z.int().nonnegative();
export const ownActivitySchema = z.object({
  days: z.int().positive(),
  since: z.iso.datetime(),
  // One row per recording (a new visit or a recording added to one), so the
  // client can group by its own local day.
  recordings: z.array(
    z.object({
      at: z.iso.datetime(),
      durationSeconds: seconds,
      newVisit: z.boolean(),
    }),
  ),
  visits: seconds,
  recordedSeconds: seconds,
  // Submission of a new visit to its first finished note.
  noteReady: z.object({
    medianSeconds: seconds.nullable(),
    count: seconds,
  }),
});
export type OwnActivity = z.output<typeof ownActivitySchema>;
export const accountActivityContract = {
  ownActivity: oc
    .errors({
      BAD_REQUEST: { message: "Invalid request" },
      NOT_FOUND: { message: "Not Found" },
      METHOD_NOT_ALLOWED: { message: "Method Not Allowed" },
      INTERNAL_SERVER_ERROR: { message: "Internal Server Error" },
      UNAUTHORIZED: { message: "Authentication required" },
      FORBIDDEN: { message: "Account required" },
      SERVICE_UNAVAILABLE: { message: "Activity unavailable" },
    })
    .input(
      z.object({
        query: ownActivityQuerySchema.default({ days: 30 }),
        body: z.undefined().optional(),
      }),
    )
    .meta(
      openapi({
        method: "GET",
        path: "/api/v2/me/activity",
        inputStructure: "detailed",
        spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
      }),
    )
    .output(ownActivitySchema),
};
